<script setup lang="js">
import { useAuthStore } from '@/stores/authStore.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { useOrganizationStore } from '@/stores/organizationStore.js'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { onUnmounted, watch } from 'vue'
import router from '@/router/router.js'
import { systemEchoes } from '@/composables/echo/useSystemEchoes.js'

const authStore = useAuthStore()
const orgStore = useOrganizationStore()
const emit = defineEmits(['show-contact-us'])

const props = defineProps({
	show: { type: Boolean, default: false }
})

onUnmounted(() => {
	orgChangeWatch()
})

/**
 * Keeps an eye on the active
 * @type {WatchHandle}
 */
const orgChangeWatch = watch(() => authStore.activeOrganizationId, (p_new_val, p_old_val) =>
{
	if (props.show)
	{
		if (p_old_val === 0 && p_new_val > 0)
		{
			//send user to dashboard since user may not have permission to view the current page.
			window.location.assign(router.resolve({ name: 'dashboard' }).href)
		}
	}
})

function selectOrganization(organization)
{
	//Stop listening to current role channel
	systemEchoes.notificationRoleDisconnect();

	authStore.setActiveOrganizationId(organization.id)

	// Move the active org to the top
	const organizations = authStore.userOrganizations
	const activeOrg = organizations.find(org => org.id === organization.id)
	const otherOrgs = organizations.filter(org => org.id !== organization.id).sort(function(a, b)
	{
		return a.organization_name.localeCompare(b.organization_name)
	})

	authStore.setUserOrganizations([activeOrg, ...otherOrgs])
	orgStore.toggleChooseModal(false)

	//Start listening to selected role channel
	systemEchoes.notificationRoleConnect();
}

function logout()
{
	authStore.logout()
}

function getOrgDisplayName(p_org_name, p_org_name_dba)
{
	if (p_org_name_dba)
	{
		return `${p_org_name}  (${p_org_name_dba})`
	}
	return p_org_name
}
</script>

<template>
	<BaseModal :show="show">
		<form>
			<p class="w-full text-center text-3xl font-bold text-heading dark:text-label-on-color mb-4">
				Please choose an account
			</p>

			<p class="w-full text-center font-medium text-caption mb-6.5">
				It looks like you have access to multiple organizations,
				<br />
				please choose which organization you would like to access.
			</p>

			<div class="flex flex-col">
				<div class="w-fit mx-auto min-w-80 rounded-md border border-neutral dark:border-neutral-dark max-h-80 overflow-y-auto">
					<div v-for="organization in authStore.userOrganizations.filter((org) => org.deleted_at === null && org.user_organization_deleted_at === null)" :key="organization.id" class="relative flex items-start">
						<div class="flex items-center w-full px-4.5 py-2 cursor-pointer hover:bg-primary-subtle"
								@click.prevent="selectOrganization(organization)">
							<div class="text-sm/6">
								<label :for="organization.id" class="font-medium text-black">{{ getOrgDisplayName(organization.organization_name, organization.organization_name_dba) }}</label>
								<p :id="`${organization.id}-name`" class="text-grayscale-body">{{ organization.street }}</p>
								<p :id="`${organization.id}-address`"
									class="text-grayscale-body">{{ `${organization.city}, ${organization.state} ${organization.zip}` }}</p>
								<p :id="`${organization.id}-citystatezip`" v-if="organization.facility_type != null" class="text-grayscale-body">{{ `Specialty: ${organization.facility_type}` }}</p>
							</div>
						</div>
					</div>
				</div>

				<SubmitButton class="mt-8 mx-10" label="Logout" @click="logout" />

				<p class="text-center text-label font-medium mt-8">Need help? <span class="underline text-primary cursor-pointer"
																										 @click="emit('show-contact-us')">Contact us</span>.</p>
			</div>
		</form>
	</BaseModal>
</template>